import { c as _c } from "react/compiler-runtime";
import { PropsWithClassName } from "@/types/ui";
import React, { useState } from "react";
import { useTranslations } from "next-intl";
import { ClosableAlert } from "@components/alerts/closable-alert";
import { UploadButton } from "@components/common/buttons/upload-button";
import { SpinnerContainer } from "@components/common/containers/spinner-container";
import { cn } from "@lib/utils";
import { ContributorBiographyInlineOutputView } from "@features/proposals/types/member";
import { MemberPhotoView } from "@features/proposals/ui/member/member-photo-view";
interface MemberPhotoUploadProps {
  biography: ContributorBiographyInlineOutputView | undefined;
  firstName?: string;
  lastName?: string;
  onPhotoSelect: (body: FormData) => Promise<void>;
  disabled?: boolean;
  isMe?: boolean;
}

/**
 * MemberPhotoUpload component - handles member photo display and upload
 *
 * Business context:
 * - Displays current member photo or avatar with initials
 * - Allows photo upload (file selection only, actual upload handled by parent)
 * - Shows informative text about photo requirements
 *
 */
export const MemberPhotoUpload = t0 => {
  const $ = _c(33);
  const {
    biography,
    firstName,
    lastName,
    onPhotoSelect,
    disabled,
    className,
    isMe: t1
  } = t0;
  const isMe = t1 === undefined ? false : t1;
  const t = useTranslations("features.proposals.ui.memberContentEditBlock");
  const [loading, setLoading] = useState(false);
  let t2;
  if ($[0] !== className || $[1] !== loading) {
    t2 = cn("flex min-h-36 gap-6", {
      "items-center": loading
    }, className);
    $[0] = className;
    $[1] = loading;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== loading) {
    t3 = loading && <SpinnerContainer variant="default" />;
    $[3] = loading;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== biography || $[6] !== firstName || $[7] !== lastName || $[8] !== loading) {
    t4 = !loading && <MemberPhotoView biography={biography} firstName={firstName} lastName={lastName} size="lg" />;
    $[5] = biography;
    $[6] = firstName;
    $[7] = lastName;
    $[8] = loading;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isMe || $[11] !== loading || $[12] !== t) {
    t5 = !loading && <ClosableAlert description={isMe ? t("photo.requirementsForMe") : t("photo.requirements")} />;
    $[10] = isMe;
    $[11] = loading;
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== loading || $[15] !== t) {
    t6 = !loading && <p className="text-sm text-muted-foreground">{t("photo.savedSeparately")}</p>;
    $[14] = loading;
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== loading) {
    t7 = cn({
      hidden: loading
    });
    $[17] = loading;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== disabled || $[20] !== loading || $[21] !== onPhotoSelect || $[22] !== t7) {
    t8 = <UploadButton accept="image/*" onFileSelect={onPhotoSelect} disabled={disabled} loading={loading} setLoading={setLoading} variant="outline" className={t7} />;
    $[19] = disabled;
    $[20] = loading;
    $[21] = onPhotoSelect;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== t5 || $[25] !== t6 || $[26] !== t8) {
    t9 = <div className="flex flex-col gap-4">{t5}{t6}{t8}</div>;
    $[24] = t5;
    $[25] = t6;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t2 || $[29] !== t3 || $[30] !== t4 || $[31] !== t9) {
    t10 = <div className={t2}>{t3}{t4}{t9}</div>;
    $[28] = t2;
    $[29] = t3;
    $[30] = t4;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
};